import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Select,
  message,
  Space,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Customer } from '../types';
import { customerApi } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CustomerForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing && id) {
      fetchCustomer(parseInt(id));
    }
  }, [isEditing, id]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await customerApi.getById(customerId);
      setCustomer(response.data);
      form.setFieldsValue(response.data);
    } catch (error) {
      message.error('Failed to fetch customer details');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (isEditing && id) {
        await customerApi.update(parseInt(id), values);
        message.success('Customer updated successfully');
      } else {
        await customerApi.create(values);
        message.success('Customer created successfully');
      }

      navigate('/admin/customers');
    } catch (error) {
      message.error(isEditing ? 'Failed to update customer' : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/customers');
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <div>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ marginBottom: 16 }}
            >
              Back to Customers
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditing ? 'Edit Customer' : 'Add New Customer'}
            </Title>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              preferred_contact_method: 'phone',
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input size="large" placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <Input size="large" placeholder="Enter last name" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="company_name"
              label="Company Name (Optional)"
            >
              <Input size="large" placeholder="Enter company name" />
            </Form.Item>

            <Divider />

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[
                    { required: true, message: 'Please enter phone number' },
                    { pattern: /^[\d\s\-\(\)\+\.]+$/, message: 'Please enter a valid phone number' }
                  ]}
                >
                  <Input size="large" placeholder="(555) 123-4567" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter email address' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input size="large" placeholder="customer@example.com" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="preferred_contact_method"
              label="Preferred Contact Method"
              rules={[{ required: true }]}
            >
              <Select size="large" placeholder="Select preferred contact method">
                <Option value="phone">Phone</Option>
                <Option value="email">Email</Option>
                <Option value="sms">SMS</Option>
              </Select>
            </Form.Item>

            <Divider />

            <Form.Item
              name="address"
              label="Street Address"
              rules={[{ required: true, message: 'Please enter address' }]}
            >
              <Input size="large" placeholder="123 Main Street" />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="city"
                  label="City"
                  rules={[{ required: true, message: 'Please enter city' }]}
                >
                  <Input size="large" placeholder="City" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true, message: 'Please enter state' }]}
                >
                  <Input size="large" placeholder="State" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="zip"
                  label="ZIP Code"
                  rules={[
                    { required: true, message: 'Please enter ZIP code' },
                    { pattern: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code' }
                  ]}
                >
                  <Input size="large" placeholder="12345" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label="Notes (Optional)"
            >
              <TextArea
                rows={4}
                placeholder="Additional notes about this customer..."
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                icon={<SaveOutlined />}
                block
              >
                {isEditing ? 'Update Customer' : 'Create Customer'}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default CustomerForm;